import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from 'react-oidc-context';
import { Navigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as S from './Management.styles';
import {
  listTips,
  createTip,
  updateTip,
  deleteTip,
  getUploadUrls,
  uploadTipFile,
  getTipFileUrl
} from '../../api/teachingTipsPortalApi';

// ==================== Styled Components ====================

const TipsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TipCard = styled.div`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #F1AF59;
    box-shadow: 0 4px 12px rgba(241, 175, 89, 0.15);
  }
`;

const TipCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const TipTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
`;

const TipMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 8px;
`;

const TipPreview = styled.div`
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const PinnedBadge = styled.span`
  background: #F1AF59;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 8px;
  flex-shrink: 0;
`;

const AttachmentBadge = styled.span`
  background: #e5e7eb;
  color: #374151;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
`;

const DetailPanel = styled.div`
  background: white;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const DetailTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
`;

const DetailMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const RichContent = styled.div`
  font-size: 15px;
  line-height: 1.8;
  color: #374151;

  h1 { font-size: 24px; font-weight: 700; color: #1f2937; margin: 20px 0 12px; }
  h2 { font-size: 20px; font-weight: 600; color: #1f2937; margin: 18px 0 10px; }
  h3 { font-size: 17px; font-weight: 600; color: #1f2937; margin: 16px 0 8px; }
  ul, ol { padding-left: 24px; margin: 8px 0; }
  li { margin-bottom: 4px; }
  a { color: #3b82f6; text-decoration: underline; }
  strong { font-weight: 600; }
  em { font-style: italic; }
  u { text-decoration: underline; }
  p { margin: 8px 0; }
`;

const AttachmentsSection = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const AttachmentsSectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
`;

const AttachmentItem = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 8px;
  text-decoration: none;
  color: #1f2937;
  transition: background 0.2s;

  &:hover {
    background: #f3f4f6;
  }
`;

const FileIcon = styled.span`
  font-size: 20px;
  flex-shrink: 0;
`;

const FileName = styled.span`
  font-size: 14px;
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.span`
  font-size: 12px;
  color: #6b7280;
  flex-shrink: 0;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const WideModal = styled(S.Modal)`
  max-width: 800px;
`;

const EditorWrapper = styled.div`
  .ql-container {
    min-height: 200px;
    font-size: 15px;
    font-family: inherit;
  }

  .ql-editor {
    min-height: 200px;
  }

  .ql-toolbar {
    border-radius: 8px 8px 0 0;
    border-color: #d1d5db;
  }

  .ql-container.ql-snow {
    border-radius: 0 0 8px 8px;
    border-color: #d1d5db;
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: #F1AF59;
  }
`;

const FileUploadText = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const FileList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FileListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 6px;
  font-size: 13px;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 16px;
  padding: 0 4px;

  &:hover {
    color: #b91c1c;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
`;

const DetailActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #6b7280;
  font-size: 14px;
`;

// ==================== Helper Functions ====================

const stripHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const getFileIcon = (contentType) => {
  if (contentType?.includes('pdf')) return '\uD83D\uDCC4';
  if (contentType?.includes('word') || contentType?.includes('document')) return '\uD83D\uDCC3';
  if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return '\uD83D\uDCCA';
  if (contentType?.includes('powerpoint') || contentType?.includes('presentation')) return '\uD83D\uDCCA';
  if (contentType?.includes('image')) return '\uD83D\uDDBC\uFE0F';
  if (contentType?.includes('zip')) return '\uD83D\uDCE6';
  return '\uD83D\uDCC1';
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Quill config
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline',
  'list', 'bullet', 'link'
];

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_FILES = 10;

// ==================== Main Component ====================

const TeachingTips = () => {
  const auth = useAuth();
  const fileInputRef = useRef(null);
  const [ReactQuill, setReactQuill] = useState(null);

  // State - all hooks must be called before any conditional returns
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTip, setSelectedTip] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formPinned, setFormPinned] = useState(false);
  const [formFiles, setFormFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [removedAttachments, setRemovedAttachments] = useState([]);

  // Role checks
  const userGroups = auth.user?.profile?.['cognito:groups'] || [];
  const hasRole = (role) => userGroups.includes(`portal_${role}`);

  const canView = hasRole('developer') || hasRole('system_admin') ||
                  hasRole('director') || hasRole('manager') ||
                  hasRole('admin') || hasRole('teacher');
  const canManage = hasRole('developer') || hasRole('system_admin') || hasRole('director');

  // Lazy load ReactQuill
  useEffect(() => {
    import('react-quill-new').then(module => {
      setReactQuill(() => module.default);
    }).catch(err => {
      console.error('Failed to load rich text editor:', err);
    });
    import('react-quill-new/dist/quill.snow.css').catch(() => {});
  }, []);

  // Fetch tips
  const fetchTips = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listTips();
      setTips(data);
    } catch (err) {
      console.error('Error fetching tips:', err);
      setError('Failed to load teaching tips. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (canView) {
      fetchTips();
    }
  }, [canView, fetchTips]);

  // Redirect parents (after all hooks)
  if (!canView) {
    return <Navigate to="/portal" replace />;
  }

  // Open create modal
  const openCreateModal = () => {
    setFormTitle('');
    setFormContent('');
    setFormPinned(false);
    setFormFiles([]);
    setExistingAttachments([]);
    setRemovedAttachments([]);
    setShowCreateModal(true);
  };

  // Open edit modal
  const openEditModal = (tip) => {
    setFormTitle(tip.title);
    setFormContent(tip.content);
    setFormPinned(tip.pinned || false);
    setFormFiles([]);
    setExistingAttachments(tip.attachments || []);
    setRemovedAttachments([]);
    setShowEditModal(true);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const currentCount = formFiles.length + existingAttachments.length - removedAttachments.length;

    const validFiles = [];
    for (const file of files) {
      if (currentCount + validFiles.length >= MAX_FILES) {
        alert(`Maximum ${MAX_FILES} files allowed.`);
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" exceeds 20MB limit.`);
        continue;
      }
      validFiles.push(file);
    }

    setFormFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove a new file from upload list
  const removeNewFile = (index) => {
    setFormFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Remove an existing attachment
  const removeExistingAttachment = (s3Key) => {
    setRemovedAttachments(prev => [...prev, s3Key]);
  };

  // Save (create or update)
  const handleSave = async (isEdit = false) => {
    if (!formTitle.trim()) {
      alert('Please enter a title.');
      return;
    }
    if (!formContent.trim() || formContent === '<p><br></p>') {
      alert('Please enter content.');
      return;
    }

    setSaving(true);
    try {
      const tipId = isEdit ? selectedTip.id : uuidv4();

      // Upload new files if any
      let newAttachments = [];
      if (formFiles.length > 0) {
        const filesMeta = formFiles.map(f => ({
          fileName: f.name,
          contentType: f.type || 'application/octet-stream'
        }));

        const uploadUrls = await getUploadUrls(tipId, filesMeta);

        await Promise.all(uploadUrls.map((urlInfo, idx) =>
          uploadTipFile(urlInfo.uploadUrl, formFiles[idx], urlInfo.contentType)
        ));

        newAttachments = uploadUrls.map((urlInfo, idx) => ({
          s3Key: urlInfo.s3Key,
          fileName: urlInfo.fileName,
          fileSize: formFiles[idx].size,
          contentType: urlInfo.contentType
        }));
      }

      if (isEdit) {
        const keptAttachments = existingAttachments.filter(
          a => !removedAttachments.includes(a.s3Key)
        );
        const allAttachments = [...keptAttachments, ...newAttachments];

        await updateTip(tipId, {
          title: formTitle.trim(),
          content: formContent,
          pinned: formPinned,
          attachments: allAttachments
        });
      } else {
        await createTip({
          id: tipId,
          title: formTitle.trim(),
          content: formContent,
          pinned: formPinned,
          attachments: newAttachments
        });
      }

      setShowCreateModal(false);
      setShowEditModal(false);
      setSelectedTip(null);
      await fetchTips();
    } catch (err) {
      console.error('Error saving tip:', err);
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteTip(selectedTip.id);
      setShowDeleteConfirm(false);
      setSelectedTip(null);
      await fetchTips();
    } catch (err) {
      console.error('Error deleting tip:', err);
      alert(`Failed to delete: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ==================== Render Sections ====================

  const renderListView = () => (
    <TipsContainer>
      {tips.length === 0 && !loading ? (
        <S.EmptyState>
          No teaching tips yet.
          {canManage && ' Click "New Announcement" to create one.'}
        </S.EmptyState>
      ) : (
        tips.map(tip => (
          <TipCard key={tip.id} onClick={() => setSelectedTip(tip)}>
            <TipCardHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                {tip.pinned && <PinnedBadge>Pinned</PinnedBadge>}
                <TipTitle>{tip.title}</TipTitle>
              </div>
              {tip.attachments?.length > 0 && (
                <AttachmentBadge>
                  {tip.attachments.length} file{tip.attachments.length > 1 ? 's' : ''}
                </AttachmentBadge>
              )}
            </TipCardHeader>
            <TipMeta>
              By {tip.authorName} &middot; {formatDate(tip.createdAt)}
              {tip.updatedAt !== tip.createdAt && ` (edited)`}
            </TipMeta>
            <TipPreview>{stripHtml(tip.content)}</TipPreview>
          </TipCard>
        ))
      )}
    </TipsContainer>
  );

  const renderDetailView = () => (
    <div>
      <BackButton onClick={() => setSelectedTip(null)}>
        &larr; Back to list
      </BackButton>
      <DetailPanel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          {selectedTip.pinned && <PinnedBadge>Pinned</PinnedBadge>}
        </div>
        <DetailTitle>{selectedTip.title}</DetailTitle>
        <DetailMeta>
          Posted by {selectedTip.authorName} &middot; {formatDate(selectedTip.createdAt)}
          {selectedTip.updatedAt !== selectedTip.createdAt &&
            <> &middot; Edited {formatDate(selectedTip.updatedAt)}</>
          }
        </DetailMeta>

        <RichContent dangerouslySetInnerHTML={{ __html: selectedTip.content }} />

        {selectedTip.attachments?.length > 0 && (
          <AttachmentsSection>
            <AttachmentsSectionTitle>
              Attachments ({selectedTip.attachments.length})
            </AttachmentsSectionTitle>
            {selectedTip.attachments.map((att, idx) => (
              <AttachmentItem
                key={idx}
                href={getTipFileUrl(att.s3Key)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileIcon>{getFileIcon(att.contentType)}</FileIcon>
                <FileName>{att.fileName}</FileName>
                <FileSize>{formatFileSize(att.fileSize)}</FileSize>
              </AttachmentItem>
            ))}
          </AttachmentsSection>
        )}

        {canManage && (
          <DetailActions>
            <S.SmallButton color="#3b82f6" onClick={() => openEditModal(selectedTip)}>
              Edit
            </S.SmallButton>
            <S.SmallButton color="#ef4444" onClick={() => setShowDeleteConfirm(true)}>
              Delete
            </S.SmallButton>
          </DetailActions>
        )}
      </DetailPanel>
    </div>
  );

  const renderFormModal = (isEdit) => (
    <S.ModalOverlay>
      <WideModal>
        <S.ModalHeader>
          <h3>{isEdit ? 'Edit Announcement' : 'New Announcement'}</h3>
          <S.CloseButton onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}>
            &times;
          </S.CloseButton>
        </S.ModalHeader>
        <S.ModalContent>
          <S.FormGroup>
            <label>Title *</label>
            <S.Input
              value={formTitle}
              onChange={e => setFormTitle(e.target.value)}
              placeholder="Announcement title..."
            />
          </S.FormGroup>

          <S.FormGroup>
            <label>Content *</label>
            <EditorWrapper>
              {ReactQuill ? (
                <ReactQuill
                  theme="snow"
                  value={formContent}
                  onChange={setFormContent}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Write your announcement..."
                />
              ) : (
                <S.TextArea
                  value={formContent}
                  onChange={e => setFormContent(e.target.value)}
                  placeholder="Loading rich text editor..."
                  rows={8}
                />
              )}
            </EditorWrapper>
          </S.FormGroup>

          <S.FormGroup>
            <label>Attachments</label>
            <FileUploadArea onClick={() => fileInputRef.current?.click()}>
              <FileUploadText>
                Click to select files (PDF, DOC, XLS, PPT, images, ZIP - max 20MB each, up to {MAX_FILES} files)
              </FileUploadText>
            </FileUploadArea>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              multiple
              onChange={handleFileSelect}
            />

            {existingAttachments.filter(a => !removedAttachments.includes(a.s3Key)).length > 0 && (
              <FileList>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginTop: 8 }}>
                  Existing files:
                </div>
                {existingAttachments
                  .filter(a => !removedAttachments.includes(a.s3Key))
                  .map((att, idx) => (
                    <FileListItem key={`existing-${idx}`}>
                      <span>
                        {getFileIcon(att.contentType)} {att.fileName} ({formatFileSize(att.fileSize)})
                      </span>
                      <RemoveFileButton onClick={() => removeExistingAttachment(att.s3Key)}>
                        &times;
                      </RemoveFileButton>
                    </FileListItem>
                  ))
                }
              </FileList>
            )}

            {formFiles.length > 0 && (
              <FileList>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginTop: 8 }}>
                  New files:
                </div>
                {formFiles.map((file, idx) => (
                  <FileListItem key={`new-${idx}`}>
                    <span>
                      {getFileIcon(file.type)} {file.name} ({formatFileSize(file.size)})
                    </span>
                    <RemoveFileButton onClick={() => removeNewFile(idx)}>
                      &times;
                    </RemoveFileButton>
                  </FileListItem>
                ))}
              </FileList>
            )}
          </S.FormGroup>

          <S.FormGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formPinned}
                onChange={e => setFormPinned(e.target.checked)}
              />
              Pin this announcement to the top
            </CheckboxLabel>
          </S.FormGroup>
        </S.ModalContent>
        <S.ModalFooter>
          <S.Button
            color="#6b7280"
            onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}
            disabled={saving}
          >
            Cancel
          </S.Button>
          <S.Button onClick={() => handleSave(isEdit)} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Publish'}
          </S.Button>
        </S.ModalFooter>
      </WideModal>
    </S.ModalOverlay>
  );

  const renderDeleteConfirm = () => (
    <S.ModalOverlay>
      <S.Modal>
        <S.ModalHeader>
          <h3>Delete Announcement</h3>
          <S.CloseButton onClick={() => setShowDeleteConfirm(false)}>&times;</S.CloseButton>
        </S.ModalHeader>
        <S.ModalContent>
          <p>Are you sure you want to delete "<strong>{selectedTip?.title}</strong>"?</p>
          <p style={{ fontSize: 13, color: '#6b7280' }}>
            This will also delete all attached files. This action cannot be undone.
          </p>
        </S.ModalContent>
        <S.ModalFooter>
          <S.Button color="#6b7280" onClick={() => setShowDeleteConfirm(false)} disabled={saving}>
            Cancel
          </S.Button>
          <S.Button color="#ef4444" onClick={handleDelete} disabled={saving}>
            {saving ? 'Deleting...' : 'Delete'}
          </S.Button>
        </S.ModalFooter>
      </S.Modal>
    </S.ModalOverlay>
  );

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Title>Teaching Tips</S.Title>
          <S.Subtitle>
            {canManage
              ? 'Post announcements and resources for teaching staff'
              : 'View announcements and resources from administration'}
          </S.Subtitle>
        </div>
        {canManage && (
          <S.Button onClick={openCreateModal}>
            + New Announcement
          </S.Button>
        )}
      </S.Header>

      {error && <S.ErrorBanner>{error}</S.ErrorBanner>}

      {loading ? (
        <LoadingOverlay>Loading teaching tips...</LoadingOverlay>
      ) : selectedTip ? (
        renderDetailView()
      ) : (
        renderListView()
      )}

      {showCreateModal && renderFormModal(false)}
      {showEditModal && renderFormModal(true)}
      {showDeleteConfirm && renderDeleteConfirm()}
    </S.Container>
  );
};

export default TeachingTips;
